/**
 * Collection picker and creation modal (Stage Z PR C).
 *
 * Used from product cards and the product popup. Requirements it satisfies:
 *   * adds a lead to any existing collection, toggling membership;
 *   * creates a collection inline WITHOUT losing the selected product — the new
 *     collection is selected and the lead is added immediately;
 *   * reports real errors instead of pretending success.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Folder, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  COLLECTION_COLORS,
  COLLECTION_ICONS,
  addToCollection,
  createCollection,
  leadCollections,
  listCollections,
  removeFromCollection,
} from "@/lib/leads/collectionsApi";

const COLOR_CLASS = {
  emerald: "bg-emerald-500",
  blue: "bg-sky-500",
  amber: "bg-amber-500",
  rose: "bg-rose-500",
  violet: "bg-violet-500",
  slate: "bg-slate-500",
  teal: "bg-teal-500",
};

/** Create-collection form body, reused by the picker and the card action. */
export function CollectionForm({ onCreate, onCancel, busy }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("folder");
  const [color, setColor] = useState("emerald");

  const submit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate({ name: trimmed, description, icon, color });
  };

  return (
    <form onSubmit={submit} data-testid="collection-create-form" className="space-y-2.5">
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
          Name
        </span>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          placeholder="Skincare"
          data-testid="collection-name-input"
          className="mt-0.5 w-full h-8 rounded-md border border-edge-subtle px-2 text-compact"
        />
      </label>
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
          Description (optional)
        </span>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          placeholder="Products to review this week"
          data-testid="collection-description-input"
          className="mt-0.5 w-full h-8 rounded-md border border-edge-subtle px-2 text-compact"
        />
      </label>
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
            Icon
          </span>
          <div className="mt-1 flex flex-wrap gap-1">
            {COLLECTION_ICONS.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setIcon(value)}
                aria-pressed={icon === value}
                className={cn(
                  "h-6 px-1.5 rounded border text-[10px]",
                  icon === value
                    ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                    : "border-edge-subtle text-ink-muted",
                )}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
            Colour
          </span>
          <div className="mt-1 flex gap-1">
            {COLLECTION_COLORS.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setColor(value)}
                aria-label={value}
                aria-pressed={color === value}
                data-testid={`collection-color-${value}`}
                className={cn(
                  "h-5 w-5 rounded-full",
                  COLOR_CLASS[value] || "bg-slate-400",
                  color === value ? "ring-2 ring-offset-1 ring-ink-primary" : "opacity-70",
                )}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="h-8 px-3 rounded-md border border-edge-subtle text-compact"
          >
            Cancel
          </button>
        ) : null}
        <button
          type="submit"
          disabled={!name.trim() || busy}
          data-testid="collection-submit"
          className="h-8 px-3 rounded-md bg-emerald-600 text-white text-compact disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create collection"}
        </button>
      </div>
    </form>
  );
}

/**
 * Picker body. `lead` is the selected product; creating a collection while
 * picking keeps it and adds the lead to the new folder.
 */
export function CollectionPicker({
  lead,
  collections,
  memberIds,
  onToggle,
  onCreate,
  onDone,
  onRefresh,
  busyIds = new Set(),
  creating = false,
}) {
  const [showForm, setShowForm] = useState(false);

  // Refetch on mount so opening the picker again (new lead, reopened popup, or
  // after a change elsewhere) never renders stale membership.
  useEffect(() => {
    if (typeof onRefresh === "function") void onRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="collection-picker" className="space-y-2">
      {collections.length === 0 && !showForm ? (
        <p className="text-compact text-ink-muted" data-testid="collection-picker-empty">
          No collections yet. Create one to start organizing this product.
        </p>
      ) : null}

      <ul className="max-h-56 overflow-auto space-y-0.5">
        {collections.map((col) => {
          const isMember = memberIds.has(col.collection_id);
          const isBusy = busyIds.has(col.collection_id);
          return (
            <li key={col.collection_id}>
              <button
                type="button"
                onClick={() => onToggle(col, !isMember)}
                disabled={isBusy}
                data-testid={`collection-option-${col.collection_id}`}
                data-member={isMember ? "true" : "false"}
                className={cn(
                  "w-full flex items-center gap-2 h-8 px-2 rounded-md border text-left text-compact",
                  isMember
                    ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                    : "border-edge-subtle hover:bg-black/[0.03]",
                )}
              >
                <Folder className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1 truncate">{col.name}</span>
                <span className="text-[10px] text-ink-muted tabular-nums">
                  {col.item_count ?? 0}
                </span>
                {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              </button>
            </li>
          );
        })}
      </ul>

      {showForm || collections.length === 0 ? (
        <CollectionForm
          busy={creating}
          onCancel={collections.length ? () => setShowForm(false) : null}
          onCreate={async (fields) => {
            await onCreate(fields);
            setShowForm(false);
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          data-testid="collection-picker-new"
          className="w-full h-8 rounded-md border border-dashed border-edge-subtle text-compact flex items-center justify-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> Create new collection
        </button>
      )}

      {onDone ? (
        <button
          type="button"
          onClick={onDone}
          className="w-full h-8 rounded-md bg-ink-primary text-white text-compact"
        >
          Done
        </button>
      ) : null}
    </div>
  );
}

/**
 * Hook owning collection data + membership for one lead.
 * Reused by the card action and the product popup so both behave identically.
 */
export function useLeadCollections(lead) {
  const itemId = String(lead?.walmartItemId || "").trim();
  const sourceKey = lead?.sourceId && lead?.sourceProductId
    ? `${lead.sourceId}:${lead.sourceProductId}`
    : "";

  const [collections, setCollections] = useState([]);
  const [memberIds, setMemberIds] = useState(() => new Set());
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    try {
      const [all, mine] = await Promise.all([
        listCollections(),
        leadCollections({ walmartItemId: itemId, sourceKey }),
      ]);
      setCollections(all?.data?.items || all?.items || []);
      const ids = mine?.data?.collection_ids || mine?.collection_ids || [];
      setMemberIds(new Set(ids));
    } catch {
      /* the picker degrades to "no collections" rather than lying */
    } finally {
      setLoading(false);
    }
  }, [itemId, sourceKey]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const markBusy = (id, on) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const toggle = useCallback(
    async (collection, makeMember) => {
      if (!itemId) {
        toast.error("Save this product before adding it to a collection.");
        return false;
      }
      markBusy(collection.collection_id, true);
      const before = new Set(memberIds);
      // Optimistic, but reverted on failure.
      setMemberIds((prev) => {
        const next = new Set(prev);
        if (makeMember) next.add(collection.collection_id);
        else next.delete(collection.collection_id);
        return next;
      });
      try {
        if (makeMember) {
          await addToCollection(collection.collection_id, {
            walmartItemId: itemId,
            sourceKey,
          });
        } else {
          await removeFromCollection(collection.collection_id, {
            walmartItemId: itemId,
            sourceKey,
          });
        }
        return true;
      } catch (err) {
        setMemberIds(before);
        const detail = err?.response?.data?.detail;
        toast.error(
          typeof detail === "string"
            ? detail.replace(/_/g, " ")
            : "Could not update the collection. Please try again.",
        );
        return false;
      } finally {
        markBusy(collection.collection_id, false);
      }
    },
    [itemId, sourceKey, memberIds],
  );

  /** Create a folder inline and immediately add THIS lead to it. */
  const createAndAdd = useCallback(
    async (fields) => {
      setCreating(true);
      try {
        const res = await createCollection(fields);
        const created = res?.data?.item || res?.item;
        if (!created?.collection_id) throw new Error("Collection was not created.");
        setCollections((prev) => [...prev, { ...created, item_count: 0 }]);
        if (itemId) {
          await addToCollection(created.collection_id, { walmartItemId: itemId, sourceKey });
          setMemberIds((prev) => new Set(prev).add(created.collection_id));
        }
        toast.success(`Added to ${created.name}.`);
        await refresh();
        return created;
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(
          typeof detail === "string"
            ? detail.replace(/_/g, " ")
            : "Could not create the collection.",
        );
        return null;
      } finally {
        setCreating(false);
      }
    },
    [itemId, sourceKey, refresh],
  );

  return useMemo(
    () => ({
      collections,
      memberIds,
      busyIds,
      creating,
      loading,
      refresh,
      toggle,
      createAndAdd,
    }),
    [collections, memberIds, busyIds, creating, loading, refresh, toggle, createAndAdd],
  );
}
